import { useState } from 'react';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import { formatDate, formatMoney } from '@/utils/format';
import type { EarlyPayoutRequest } from '@/types/portal';

interface EarlyPayoutRequestsProps {
  requests: EarlyPayoutRequest[];
  canReview: boolean;
  busyId: string | null;
  onReview: (request: EarlyPayoutRequest, approved: boolean, note: string) => void;
}

const TONE = { Pending: 'secondary', Approved: 'accent', Declined: 'primary' } as const;

/** The live portal's "Early payout requests" card (creator-detail), for the whole campaign. */
export default function EarlyPayoutRequests({ requests, canReview, busyId, onReview }: EarlyPayoutRequestsProps) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  if (requests.length === 0) return null;

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between border-b border-background-200 px-5 py-3">
        <h2 className="font-heading text-sm font-semibold text-foreground-950">Early payout requests</h2>
        <span className="text-xs text-foreground-500">
          {requests.filter((request) => request.status === 'Pending').length} pending
        </span>
      </header>
      <ul className="divide-y divide-background-100">
        {requests.map((request) => (
          <li key={request.id} className="px-5 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground-950">
                  {request.creatorName} · {formatMoney(request.amount, request.currency)}
                </p>
                <p className="mt-0.5 text-xs text-foreground-500">Requested {formatDate(request.requestedAt)}</p>
                {request.reason ? <p className="mt-1 text-sm text-foreground-600">{request.reason}</p> : null}
                {request.reviewNotes ? <p className="mt-1 text-xs text-foreground-500">Note: {request.reviewNotes}</p> : null}
              </div>
              <Badge tone={TONE[request.status]}>{request.status}</Badge>
            </div>
            {request.status === 'Pending' && canReview ? (
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  type="text"
                  maxLength={500}
                  value={notes[request.id] ?? ''}
                  onChange={(e) => setNotes((current) => ({ ...current, [request.id]: e.target.value }))}
                  placeholder="Optional note to creator…"
                  aria-label={`Note to ${request.creatorName}`}
                  className="h-9 min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    icon="ri-close-line"
                    disabled={busyId === request.id}
                    onClick={() => onReview(request, false, notes[request.id] ?? '')}
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    icon="ri-check-line"
                    disabled={busyId === request.id}
                    onClick={() => onReview(request, true, notes[request.id] ?? '')}
                  >
                    Approve
                  </Button>
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
